import { Alert } from '@neo/primitives/alert'
import { Button } from '@neo/primitives/button'
import type { ApiResponse } from '@shared/types'
import type { AxiosError } from 'axios'
import { useState } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { Navigate, useNavigate } from 'router-v6'
import { RECAPTCHA_SITE_KEY } from 'v2/configs/constants'
import { getDefaultRoute } from 'v2/configs/routes'
import { useAuth } from 'v2/shared/hooks/use-auth'
import { getErrorText } from 'v2/shared/utils/api-error-text'
import { getSafeRedirectUrl } from 'v2/utils/url-validation'
import { AuthLayout } from '../../shared/layouts/auth'
import { AUTH_LINK_CLASS, AUTH_PRIMARY_BUTTON_CLASS } from './components/auth-styles'
import { TwoFactorCodeInput } from './components/two-factor-code-input'
import TwoFactorModeToggle from './components/two-factor-mode-toggle'
import { useTwoFactorVerification } from './hooks/use-two-factor-verification'
import { getOtpChannelHint } from './otp-channel-hint'

const TwoFactorVerify = () => {
  const navigate = useNavigate()
  const { userAuthenticated } = useAuth()
  const {
    // Location state data
    phoneNumber,
    method,
    isSignup,
    nextUrl,
    isAuthenticatorEnabled,
    verificationPending,

    // Validation
    validateRequiredData,
    getRedirectPath,

    // Hook state and handlers
    isAuthenticatorMode,
    isSmsResending,
    isVoiceResending,
    timerStartTime,
    recaptchaRef,
    verifyLoginMutation,
    verifySignupMutation,
    resendMutation,
    loginMutation,
    handleVerify,
    handleResendCode,
    handleResendViaCall,
    handleTimerStop,
    handleModeSwitch,
  } = useTwoFactorVerification()

  const [code, setCode] = useState('')

  // Helper to check if any verification mutation is pending
  const isVerificationPending = verifyLoginMutation.isPending || verifySignupMutation.isPending

  // Get the first error from any of the mutations
  const verifyError = verifyLoginMutation.error || verifySignupMutation.error
  const resendError = resendMutation.error

  // Redirect if required data is missing
  if (!validateRequiredData()) {
    const redirectPath = getRedirectPath()
    return <Navigate to={redirectPath} replace />
  }

  // If the user is already authenticated, redirect them to the dashboard.
  // Validate nextUrl to prevent open redirect attacks (e.g., nextUrl=https://evil.com).
  const safeNextUrl = getSafeRedirectUrl(nextUrl)
  if (userAuthenticated) {
    if (safeNextUrl) {
      return <Navigate to={safeNextUrl} />
    }
    return <Navigate to={getDefaultRoute()} />
  }

  const otpChannel = getOtpChannelHint(phoneNumber, method)

  // Determine the title and description based on the flow
  const getTitleAndDescription = () => {
    if (isAuthenticatorMode) {
      return {
        title: 'Verify with Authenticator App',
        description: 'Open your authenticator app to view the verification code.',
        containerWidth: 'max-w-[390px]', // Smaller width for shorter title
      }
    }

    const via = otpChannel ? ` via ${otpChannel}` : ''
    const description = `Enter the 6-digit verification code sent${via} to this number`

    if (isSignup === false) {
      return {
        title: 'Verify with Phone Number',
        description,
        containerWidth: 'max-w-[360px]', // Smaller width for shorter title
      }
    }
    return {
      title: 'Secure your account by adding Two-Factor Authentication',
      description,
      containerWidth: 'max-w-[400px]', // Larger width for longer title
    }
  }

  const { title, description, containerWidth } = getTitleAndDescription()

  const handleVerifyClick = () => {
    handleVerify(code)
  }

  const handleSwitchToAuthenticator = () => {
    handleModeSwitch(true)
  }

  const handleSwitchToPhone = () => {
    handleModeSwitch(false)
  }

  return (
    <AuthLayout pageTitle="Two factor verification" title="" description="">
      <div className={`${containerWidth} flex w-full flex-col gap-4`}>
        {verifyError && (
          <Alert
            variant="destructive"
            content="Incorrect code. Please try again or request a new one."
          />
        )}
        {resendError && (
          <Alert
            variant="destructive"
            content={getErrorText(resendError as AxiosError<ApiResponse<unknown>>)}
          />
        )}
        <div>
          <h1 className="text-h1-500 text-foreground">{title}</h1>
          <p className="mt-2 text-p-400 leading-[1.55] text-secondary">{description}</p>
        </div>

        <TwoFactorCodeInput
          code={code}
          onChange={setCode}
          isAuthenticatorMode={isAuthenticatorMode}
          phoneNumber={phoneNumber}
          otpChannel={otpChannel}
          timerStartTime={timerStartTime}
          isSmsResending={isSmsResending}
          isVoiceResending={isVoiceResending}
          onResendCode={handleResendCode}
          onResendViaCall={handleResendViaCall}
          onTimerStop={handleTimerStop}
          onComplete={handleVerify}
        />

        <Button
          type="button"
          typography="action"
          className={AUTH_PRIMARY_BUTTON_CLASS}
          size="xl"
          onClick={handleVerifyClick}
          disabled={!code || isVerificationPending || loginMutation.isPending || code.length !== 6}
          data-testid="button-verify"
        >
          {isVerificationPending || loginMutation.isPending ? 'Verifying...' : 'Verify'}
        </Button>

        <TwoFactorModeToggle
          isSignup={isSignup}
          isAuthenticatorEnabled={isAuthenticatorEnabled}
          isAuthenticatorMode={isAuthenticatorMode}
          resendPending={resendMutation.isPending}
          onSwitchToAuthenticator={handleSwitchToAuthenticator}
          onSwitchToPhone={handleSwitchToPhone}
        />

        {/* Only show back button for signup flow */}
        {(isSignup !== false || verificationPending) && (
          <button
            type="button"
            className={`${AUTH_LINK_CLASS} mt-2 w-full text-center text-s-500`}
            onClick={() => navigate(-1)}
          >
            Back
          </button>
        )}
      </div>
      <ReCAPTCHA ref={recaptchaRef} size="invisible" sitekey={RECAPTCHA_SITE_KEY} />
    </AuthLayout>
  )
}

export default TwoFactorVerify
